import {Component, OnInit, ViewEncapsulation} from '@angular/core';
import {MatDialogConfig} from '@angular/material/dialog';
import {FlamingoAuthService} from '@flamingo/service/flamingo-auth.service';
import {FlamingoMessageService} from '@flamingo/service/flamingo-message.service';
import {FuseTranslationLoaderService} from '@fuse/services/translation-loader.service';
import {TranslateService} from '@ngx-translate/core';
import {saveAs} from 'file-saver';
import * as moment from 'moment';
import {filter, tap} from 'rxjs/operators';
import {BuyerService} from '../../children/main/children/buyer/buyer.service';
import {
    WalletTransactionDialogComponent,
    WalletTransactionDialogModel,
} from '../../children/main/children/buyer/component/wallet-transaction-dialog/wallet-transaction-dialog.component';
import {
    MassiveCompanyImportDialogComponent,
} from '../../children/main/children/partner/children/component/massive-company-import-dialog/massive-company-import-dialog.component';
import {
    NewCompanyDialogComponent,
} from '../../children/main/children/partner/children/component/new-company-dialog/new-company-dialog.component';
import {ClientService} from '../../children/main/children/seller/children/client/client.service';
import {
    ClientDialogComponent,
    NewCompanyDialogModel,
} from '../../children/main/children/seller/children/client/component/client-dialog/client-dialog.component';
import {
    NewInvoiceDialogComponent,
    NewInvoiceDialogModel,
} from '../../children/main/children/seller/children/invoice/children/new-invoice-dialog/new-invoice-dialog.component';
import {
    PublishInvoiceService,
} from '../../children/main/children/seller/children/invoice/publish-invoice/publish-invoice.service';
import {MainService} from '../../children/main/main.service';
import {BuyerAccountView} from '../../core/model/buyer-account-view.model';
import {BuyerFundView} from '../../core/model/buyer-fund-view.model';
import {PartnerPermission, Permission, UserType} from '../../core/model/user.model';
import {WalletDetail} from '../../core/model/wallet-detail.model';
import {CustomShortcutsService} from './custom-shortcuts.service';
import {locale as english} from './i18n/en';
import {locale as italian} from './i18n/it';

@Component({
  selector: 'app-custom-shortcuts',
  templateUrl: './custom-shortcuts.component.html',
  styleUrls: ['./custom-shortcuts.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CustomShortcutsComponent implements OnInit {
  buyer?: BuyerAccountView;
  currentFund?: BuyerFundView;
  wallet: WalletDetail;
  canDownloadReports = true;

  UserType = UserType;

  allowSellerCreate = false;
  allowDebtorCreate = false;

  constructor(
    public mainService: MainService,
    private _authService: FlamingoAuthService,
    private _buyerService: BuyerService,
    private _clientService: ClientService,
    private _customShortcutsService: CustomShortcutsService,
    private _messageService: FlamingoMessageService,
    private _publishInvoiceService: PublishInvoiceService,
    private _translateService: TranslateService,
    private _translationLoader: FuseTranslationLoaderService,
  ) {
    this._translationLoader.loadTranslations(italian, english);

    this.allowSellerCreate = mainService.user.partnerPermissions?.some(p => [PartnerPermission.All, PartnerPermission.SellerCreate].includes(p));
    this.allowDebtorCreate = mainService.user.partnerPermissions?.some(p => [PartnerPermission.All, PartnerPermission.DebtorCreate].includes(p));

    if (this.getUserType() === UserType.buyer) {
      this.canDownloadReports = _authService.hasUserPermissions(Permission.ReportDownload);
    }
  }

  ngOnInit(): void {
    if (this.getUserType() === UserType.buyer) {
      this.buyer = this._buyerService.user;
      this.currentFund = this._buyerService.userSelectedFund;
      this.updateFundWallet();
    }
  }

  updateFundWallet(): void {
    this._buyerService.getFundWallet()
      .subscribe(res => {
        if (res) {
          this.wallet = res;
        }
      });
  }

  getUserType(): UserType {
    return this._authService.user.type;
  }

  newInvoice(): void {
    const dialogConfig: MatDialogConfig = {
      panelClass: 'new-invoice-dialog',
      width: '800px',
      height: '700px',
      autoFocus: false,
      disableClose: true,
      data: { action: 'add', title: 'Caricamento fattura' } as NewInvoiceDialogModel,
    };

    this._messageService
      .showModalWithConfiguration(NewInvoiceDialogComponent, dialogConfig)
      .afterClosed()
      .subscribe(res => {
        this._publishInvoiceService.handlePublish(res);
      });
  }

  newClient(): void {
    const dialogConfig: MatDialogConfig = {
      width: '600px',
      maxHeight: '95vh',
      autoFocus: false,
      disableClose: true,
      data: { action: 'add' } as NewCompanyDialogModel,
    };

    this._messageService
      .showModalWithConfiguration(ClientDialogComponent, dialogConfig)
      .afterClosed()
      .subscribe(res => {
        if (res) {
          this._messageService.showMessage(this._translateService.instant('SHORTCUT.SNACKBAR.CLIENT_ADDED'));
          this._clientService.refreshClient$.next();
        }
      });
  }

  setCurrentFund(fund: BuyerFundView): void {
    this._buyerService.fundId = fund.id;
    this.currentFund = this._buyerService.userSelectedFund;
    this.updateFundWallet();
  }

  openWallet(): void {
    this.updateFundWallet();

    this._buyerService.getFundWalletTransactions()
      .subscribe(
        res => {
          const dialogConfig: MatDialogConfig = {
            width: '800px',
            maxHeight: '95vh',
            autoFocus: false,
            disableClose: true,
            data: {
              buyerEmail: this.buyer.email,
              walletTransactions: res,
              fundId: this._buyerService.fundId,
            } as WalletTransactionDialogModel,
          };

          this._messageService
            .showModalWithConfiguration(WalletTransactionDialogComponent, dialogConfig)
            .afterClosed()
            .subscribe();
        },
        err => {
          throw err;
        },
      );
  }

  exportWallet(type: 'csv' | 'xls'): void {
    this._buyerService.exportWalletTransactions(this._buyerService.fundId, type)
      .subscribe(res => {
        const blob = new Blob([res]);
        // TODO: remove when time
        if (this.buyer.email === 'nicolas.henrion@fortesecurities.com') {
          saveAs(blob, `${moment().format('YYYYMMDD')}_CAS_Movements_completo.${type}`);
          return;
        }
        saveAs(blob, `export_wallet_completo.${type}`);
      });
  }

  exportAuctions(): void {
    this._buyerService.exportAuctions()
      .subscribe(res => {
        const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        // TODO: remove when time
        if (this.buyer.email === 'nicolas.henrion@fortesecurities.com') {
          saveAs(blob, `${moment().format('YYYYMMDD')}_CAS_Export.xlsx`);
          return;
        }
        saveAs(blob, `auctions_export_${this.getDateString()}.xlsx`);
      });
  }

  exportAuctionsInProgress(): void {
    this._buyerService.exportAuctionsInProgress()
      .subscribe(res => {
        const blob = new Blob([res]);
        saveAs(blob, `in_progress_export_${this.getDateString()}.xlsx`);
      });
  }

  exportAuctionsScheduled(): void {
    this._buyerService.exportAuctionsScheduled()
      .subscribe(res => {
        const blob = new Blob([res]);
        saveAs(blob, `scheduled_export_${this.getDateString()}.xlsx`);
      });
  }

  newCompany(): void {
    const dialogConfig: MatDialogConfig = {
      width: '800px',
      maxWidth: '90vw',
      autoFocus: false,
      disableClose: true,
    };

    this._messageService.showModalWithConfiguration(NewCompanyDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(
        filter(res => res !== undefined),
        tap(_ => this._customShortcutsService.newCompany$.next()),
        tap(_ => this._messageService.showMessage(this._translateService.instant('SHORTCUT.SNACKBAR.COMPANY_ADDED'))),
      )
      .subscribe();
  }

  massiveCompanyImport(): void {
    const dialogConfig: MatDialogConfig = {
      width: '1000px',
      maxWidth: '90vw',
      autoFocus: false,
      disableClose: true,
    };

    this._messageService.showModalWithConfiguration(MassiveCompanyImportDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(
        filter(res => res !== undefined),
        tap(_ => this._messageService.showMessage(this._translateService.instant('SHORTCUT.SNACKBAR.MASSIVE_COMPANY_ADDED'))),
      )
      .subscribe();
  }

  private getDateString(): string {
    return moment().format('YYYY_MM_DD_HH_mm_ss');
  }
}
